import { useMemo, useState } from "react";
import type { BehaviorSubject, Goal, GoalDomain, GoalType, Phase, Status } from "api/types/aba";
import {
  GOAL_ORDERING_OPTIONS,
  type ActiveFilter,
  type FilterGroup,
  type FilterOption,
  type GoalGridFilterRowProps,
  type GoalOrderBy,
} from "./index";

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

const DOMAIN_LABELS: Record<GoalDomain, string> = {
  adaptive: "Adaptive",
  "behavior-reduction": "Behavior Reduction",
  communication: "Communication",
  community: "Community",
  "executive-functioning": "Executive Functioning",
  "social-skills": "Social Skills",
};

const STATUS_LABELS: Record<Status, string> = {
  "not-active": "Not Active",
  "in-progress": "In Progress",
  "on-hold": "On Hold",
  met: "Met",
  discontinued: "Discontinued",
  archived: "Archived",
};

const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  "skill-acquisition": "Skill Acquisition",
  "behavior-reduction": "Behavior Reduction",
};

const BEHAVIOR_SUBJECT_LABELS: Record<BehaviorSubject, string> = {
  client: "Client",
  caregiver: "Caregiver",
};

// ---------------------------------------------------------------------------
// Sort — "Status" is the default order; the Display menu lets the user pick
// any of the comparators below. Everything here runs on the already-fetched
// goals array — no comparator triggers a request to the backend.
// ---------------------------------------------------------------------------

const STATUS_SORT_ORDER: Record<Status, number> = {
  "on-hold": 0,
  "in-progress": 1,
  "not-active": 2,
  discontinued: 3,
  met: 4,
  archived: 5,
};

const PHASE_SORT_ORDER: Record<Phase, number> = {
  baseline: 0,
  intervention: 1,
  generalization: 2,
  maintenance: 3,
};

// Rank used for "no eligible targets" / unrecognized phase — higher than any
// real PHASE_SORT_ORDER value so those goals sort last in ascending order.
const UNRANKED_PHASE = 99;

// A goal has no phase of its own — stand in with the most advanced phase
// among its non-archived targets. Goals with no eligible targets sort last.
const getGoalPhaseRank = (goal: Goal): number => {
  const ranks = (goal.targets ?? [])
    .filter((target) => !target.is_archived)
    .map((target) => PHASE_SORT_ORDER[target.phase] ?? UNRANKED_PHASE);
  return ranks.length ? Math.max(...ranks) : UNRANKED_PHASE;
};

// Falls back to the raw value (or "") when a label lookup misses, so goals
// with a missing/unexpected enum value never crash the sort — they just sort
// alongside whatever text they carry (or first, if the field is empty).
const labelOrRaw = <T extends string>(labels: Record<T, string>, value: T): string =>
  labels[value] ?? String(value ?? "");

const GOAL_COMPARATORS: Record<GoalOrderBy, (a: Goal, b: Goal) => number> = {
  status: (a, b) => (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99),
  "created_at-desc": (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  "created_at-asc": (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  "title-asc": (a, b) => a.title.localeCompare(b.title),
  "title-desc": (a, b) => b.title.localeCompare(a.title),
  "phase-asc": (a, b) => getGoalPhaseRank(a) - getGoalPhaseRank(b),
  "domain-asc": (a, b) =>
    labelOrRaw(DOMAIN_LABELS, a.domain).localeCompare(labelOrRaw(DOMAIN_LABELS, b.domain)),
  "goal_type-asc": (a, b) =>
    labelOrRaw(GOAL_TYPE_LABELS, a.goal_type).localeCompare(
      labelOrRaw(GOAL_TYPE_LABELS, b.goal_type)
    ),
  "behavior_subject-asc": (a, b) =>
    labelOrRaw(BEHAVIOR_SUBJECT_LABELS, a.behavior_subject).localeCompare(
      labelOrRaw(BEHAVIOR_SUBJECT_LABELS, b.behavior_subject)
    ),
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseGoalFiltersArgs {
  goals: Goal[] | undefined;
  showArchived: boolean;
  onToggleArchived: () => void;
}

export interface UseGoalFiltersResult {
  /** Goals after active filters and sort are applied — ready to render */
  goals: Goal[];
  /** Props ready to spread onto <GoalGridFilterRow /> */
  filterRowProps: Omit<GoalGridFilterRowProps, "sx">;
}

/**
 * Encapsulates all state, smart option computation, filtering and sort logic
 * for the Goals grid filter bar. Consumers pass in the raw goals plus the
 * archived-toggle state (which lives outside the hook because it affects the
 * goals API call) and get back filtered/sorted goals and the props to spread
 * into <GoalGridFilterRow />.
 */
export function useGoalFilters({
  goals,
  showArchived,
  onToggleArchived,
}: UseGoalFiltersArgs): UseGoalFiltersResult {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [orderBy, setOrderBy] = useState<GoalOrderBy>("status");

  // Smart filter options — only values present in the current goals data
  const filterOptions = useMemo<FilterOption[]>(() => {
    if (!goals?.length) return [];

    const domains = new Set<GoalDomain>();
    const statuses = new Set<Status>();
    const goalTypes = new Set<GoalType>();

    goals.forEach((goal) => {
      if (goal.domain) domains.add(goal.domain as GoalDomain);
      if (goal.status) statuses.add(goal.status as Status);
      if (goal.goal_type) goalTypes.add(goal.goal_type as GoalType);
    });

    // Helper to map a set of values to FilterOptions with labels and grouping
    const makeOptions = <T extends string>(
      values: Set<T>,
      labels: Record<T, string>,
      group: FilterGroup
    ): FilterOption[] =>
      [...values].map((value) => ({
        id: `${group}:${value}`,
        label: labels[value] ?? value,
        group,
      }));

    return [
      ...makeOptions(domains, DOMAIN_LABELS, "domain"),
      ...makeOptions(statuses, STATUS_LABELS, "status"),
      ...makeOptions(goalTypes, GOAL_TYPE_LABELS, "goal_type"),
    ];
  }, [goals]);

  // Apply active filters — AND across groups, OR within the same group
  const filteredGoals = useMemo(() => {
    if (!goals?.length) return [];

    const byGroup: Record<FilterGroup, string[]> = {
      domain: [],
      status: [],
      goal_type: [],
    };

    activeFilters.forEach(({ id }) => {
      const [group, value] = id.split(":") as [FilterGroup, string];
      byGroup[group].push(value);
    });

    return goals.filter((goal) => {
      if (byGroup.domain.length && !byGroup.domain.includes(goal.domain ?? "")) return false;
      if (byGroup.status.length && !byGroup.status.includes(goal.status ?? "")) return false;
      if (byGroup.goal_type.length && !byGroup.goal_type.includes(goal.goal_type ?? ""))
        return false;
      return true;
    });
  }, [goals, activeFilters]);

  const sortedGoals = useMemo(
    () => [...filteredGoals].sort(GOAL_COMPARATORS[orderBy]),
    [filteredGoals, orderBy]
  );

  const filterRowProps: Omit<GoalGridFilterRowProps, "sx"> = {
    goalCount: sortedGoals.length,
    totalGoalCount: goals?.length ?? 0,
    filterOptions,
    activeFilters,
    showArchived,
    orderBy,
    orderingOptions: GOAL_ORDERING_OPTIONS,
    onOrderByChange: setOrderBy,
    onAddFilter: (option) =>
      setActiveFilters((prev) =>
        prev.some((f) => f.id === option.id)
          ? prev
          : [...prev, { id: option.id, label: option.label }]
      ),
    onDeleteFilter: (filterId) => setActiveFilters((prev) => prev.filter((f) => f.id !== filterId)),
    onToggleArchived,
    onClearFilters: () => setActiveFilters([]),
  };

  return { goals: sortedGoals, filterRowProps };
}
